import type { TranslationKey } from './en.js';

/**
 * Turns a server error code into the translation key that explains it.
 *
 * A raw code on screen is not a message, it is a leak — so anything the
 * dictionary does not have a sentence for falls back to the group's own
 * `unknown`, which is still about the thing that just failed.
 *
 * Grouped per flow rather than one flat table: "that film is not in the
 * database any more" and "that trailer link is not a YouTube link" are both
 * `film_not_found`-shaped refusals of very different things, and a shared
 * table would force one sentence to cover both.
 */
function errorKeys(group: string, known: readonly string[]): (code: string) => TranslationKey {
  return (code) => `${group}.${known.includes(code) ? code : 'unknown'}` as TranslationKey;
}

/**
 * What `TrailerService` refuses a submission for. Shared by the swipe deck's
 * form and the Filmakte's, which reach the same server checks by two routes.
 */
const TRAILER_CODES = [
  'not_youtube',
  'trailer_broken',
  'trailer_unverifiable',
  'channel_not_official',
  'not_a_trailer',
  'title_mismatch',
  'year_mismatch',
  'trailer_exists',
  'rate_limited',
] as const;

export const trailerErrorKey = errorKeys('trailerError', TRAILER_CODES);

/** What the suggest endpoint refuses a film for. */
const SUGGEST_CODES = ['already_voted', 'not_swiping', 'film_not_found'] as const;

export const suggestErrorKey = errorKeys('suggestError', SUGGEST_CODES);
